import { useState } from "react";

import { DateRangePicker } from "@/components/shared/date-range-picker";
import { PageHeader } from "@/components/shared/page-header";
import { SearchableSelect } from "@/components/shared/searchable-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useHasPermission, useMe } from "@/hooks/use-auth";
import { useCashReport } from "@/hooks/use-cash-report";
import { useRiders } from "@/hooks/use-riders";
import { useWorkspaces } from "@/hooks/use-workspaces";

import { CASH_METHOD_LABELS, CASH_SOURCE_LABELS } from "./cash-report-labels";
import { groupCashEntries, SUMMARY_CARD_METHODS } from "./cash-report-utils";
import { CashReportPdfButton } from "./pdf/cash-report-pdf-button";

import type { CashGroup } from "./cash-report-utils";
import type { CashSource } from "@artisancode/api-types";

export function CashReportPage() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [source, setSource] = useState<CashSource | "all">("all");
  const [riderId, setRiderId] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const [detailGroup, setDetailGroup] = useState<CashGroup | null>(null);

  const { data: me } = useMe();
  const canExport = useHasPermission("cash_report.export");
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaces = (workspacesData?.items ?? []).filter((w) => w.isActive);

  const { data: ridersData } = useRiders({ per_page: 100 });
  const riders = ridersData?.items ?? [];

  const { data, isLoading } = useCashReport({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    source: source === "all" ? undefined : source,
    rider_id: source === "rider" && riderId ? riderId : undefined,
    workspace_id: workspaceId || undefined,
  });

  const items = data?.items ?? [];
  const totalByMethod = data?.totalByMethod ?? {};
  const grandTotal = data?.grandTotal ?? 0;
  const groups = groupCashEntries(items);

  const selectedRiderName =
    source === "rider" ? riders.find((r) => r.id === riderId)?.name : undefined;

  const showWorkspaceLabel = Boolean(me?.canSwitchWorkspace) && !workspaceId;

  function workspaceName(id: string) {
    return workspaces.find((w) => w.id === id)?.name ?? "-";
  }

  return (
    <div>
      <PageHeader
        title="Kas"
        description="Rekonsiliasi kas masuk dari Penjualan Kantor, Penjualan Rider, dan pelunasan Piutang terverifikasi. Laporan ini otomatis dari data yang sudah tercatat — tidak ada input manual."
      />

      <p className="mb-4 hidden text-sm text-muted-foreground print:block">
        Periode: {dateFrom || "Awal"} – {dateTo || "Sekarang"} · Sumber:{" "}
        {source === "all" ? "Semua Sumber" : CASH_SOURCE_LABELS[source]}
        {selectedRiderName ? ` (${selectedRiderName})` : ""}
      </p>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-2 print:hidden">
        <div className="flex flex-wrap items-center gap-2">
          {me?.canSwitchWorkspace && (
            <div className="w-48">
              <SearchableSelect
                value={workspaceId}
                onChange={setWorkspaceId}
                options={workspaces.map((w) => ({
                  value: w.id,
                  label: w.name,
                }))}
                placeholder="Semua Workspace"
              />
            </div>
          )}
          <DateRangePicker
            from={dateFrom}
            to={dateTo}
            onChange={(f, t) => {
              setDateFrom(f);
              setDateTo(t);
            }}
          />
          <Select
            value={source}
            onValueChange={(v) => {
              setSource(v as CashSource | "all");
              if (v !== "rider") setRiderId("");
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Sumber</SelectItem>
              {Object.entries(CASH_SOURCE_LABELS).map(([v, l]) => (
                <SelectItem key={v} value={v}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {source === "rider" && (
            <div className="w-48">
              <SearchableSelect
                value={riderId}
                onChange={setRiderId}
                options={[
                  { value: "", label: "Semua rider" },
                  ...riders.map((r) => ({ value: r.id, label: r.name })),
                ]}
                placeholder="Semua rider"
                clearable
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <CashReportPdfButton
              items={items}
              totalByMethod={totalByMethod}
              grandTotal={grandTotal}
              dateFrom={dateFrom}
              dateTo={dateTo}
              sourceLabel={
                source === "all"
                  ? "Semua Sumber"
                  : `${CASH_SOURCE_LABELS[source]}${selectedRiderName ? ` (${selectedRiderName})` : ""}`
              }
            />
          )}
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Kas Masuk
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              Rp {grandTotal.toLocaleString("id-ID")}
            </div>
          </CardContent>
        </Card>
        {SUMMARY_CARD_METHODS.map((m) => (
          <Card key={m}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {CASH_METHOD_LABELS[m]}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-semibold">
                Rp {(totalByMethod[m] ?? 0).toLocaleString("id-ID")}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                Tanggal
              </th>
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                Sumber
              </th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">
                Jumlah
              </th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground print:hidden" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-6 text-center text-muted-foreground"
                >
                  Memuat...
                </td>
              </tr>
            )}
            {!isLoading && groups.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-6 text-center text-muted-foreground"
                >
                  Belum ada data kas masuk.
                </td>
              </tr>
            )}
            {groups.map((group, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="px-4 py-2">
                  {group.date}
                  {showWorkspaceLabel && (
                    <span className="mt-0.5 block text-[10px] text-muted-foreground">
                      {workspaceName(group.workspaceId)}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <div className="flex flex-col">
                    <Badge variant="secondary" className="w-fit text-[10px]">
                      {CASH_SOURCE_LABELS[group.source]}
                    </Badge>
                    <span className="mt-0.5 text-xs text-muted-foreground">
                      {group.label}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-2 text-right font-medium">
                  Rp {group.total.toLocaleString("id-ID")}
                </td>
                <td className="px-4 py-2 text-right print:hidden">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDetailGroup(group)}
                  >
                    Detail
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog
        open={detailGroup !== null}
        onOpenChange={(next) => !next && setDetailGroup(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rincian Kas Masuk</DialogTitle>
            {detailGroup && (
              <DialogDescription>
                {CASH_SOURCE_LABELS[detailGroup.source]} · {detailGroup.label} ·{" "}
                {detailGroup.date}
              </DialogDescription>
            )}
          </DialogHeader>
          {detailGroup && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Metode</TableHead>
                  <TableHead className="text-right">Jumlah</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailGroup.breakdown.map((b) => (
                  <TableRow key={b.method}>
                    <TableCell>{CASH_METHOD_LABELS[b.method]}</TableCell>
                    <TableCell className="text-right">
                      Rp {b.amount.toLocaleString("id-ID")}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell className="font-medium">Total</TableCell>
                  <TableCell className="text-right font-medium">
                    Rp {detailGroup.total.toLocaleString("id-ID")}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
